import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { compose, isNil } from 'ramda';

import {
  Container,
  MapWrapper,
  DetailsWrapper,
  Directions,
  WhereToGoContent,
  PolicyLink,
  PolicyInfo,
  Text,
} from './locationSection.styles';
import { Section } from '../../../shared/components/section';
import { Divider } from '../../../shared/components/divider';
import { ExperienceSectionList } from '../experienceSectionList';
import { renderWhenNotNil, renderWhenTrue } from '../../../shared/utils/rendering';
import messages from './locationSection.messages';
import { H3 } from '../../../theme/typography';
import { Map } from '../map';
import { getPeopleMessageProps, getPrice } from '../../../shared/utils/experienceSummary';
import { BookingCurrency } from '../../../shared/services/booking/currency';

export class LocationSectionComponent extends PureComponent {
  static propTypes = {
    experience: PropTypes.object,
    showPolicy: PropTypes.func,
    intl: PropTypes.object,
    priceBreakdownRef: PropTypes.object,
  };

  get canRenderPricingBreakdown() {
    const { discountThreshold, discountPercent, tickets } = this.props.experience;
    return !tickets.isGroup && !isNil(discountThreshold) && !isNil(discountPercent);
  }

  handlePolicyClicked = () => this.props.showPolicy();

  renderSection = (title, children, renderWhenValue = !isNil(children)) =>
    renderWhenTrue(() => (
      <>
        <Divider />
        <Section title={title}>{children}</Section>
      </>
    ))(renderWhenValue);

  renderPricingBreakdown = () => {
    const { discountThreshold, discountPercent, tickets, maxGroupSize } = this.props.experience;

    const price = this.props.intl.formatNumber(getPrice(tickets), {
      style: 'currency',
      currency: BookingCurrency.USD,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const { values, ...guestsMessage } = getPeopleMessageProps(this.props.experience);
    const guests = this.props.intl.formatMessage(guestsMessage, values);

    const message =
      maxGroupSize === discountThreshold ? messages.pricingBreakdownMaxGroupSize : messages.pricingBreakdown;

    return (
      <span ref={this.props.priceBreakdownRef}>
        {this.props.intl.formatMessage(message, {
          price,
          guests,
          discountPercent,
          discountThreshold,
        })}
      </span>
    );
  };

  renderMap = renderWhenNotNil(location => (
    <MapWrapper>
      <Map markerLatitude={location.lat} markerLongitude={location.lon} />
    </MapWrapper>
  ));

  render() {
    const { intl } = this.props;
    const { address, directions, tips, youBring, notes, location } = this.props.experience;
    const formatMessage = intl.formatMessage;

    return (
      <Container hasLocation={!!location}>
        {this.renderMap(location)}

        <DetailsWrapper hasLocation={!!location}>
          <Section title={formatMessage(messages.whereToGo)}>
            <WhereToGoContent directions={directions}>
              <H3>{address}</H3>
              {renderWhenNotNil(() => <Directions>{directions}</Directions>)(directions)}
            </WhereToGoContent>
          </Section>

          {this.renderSection(formatMessage(messages.nearbyTips), <Text>{tips}</Text>)}

          {this.renderSection(
            formatMessage(messages.youBring),
            <ExperienceSectionList text={youBring} />,
            !isNil(youBring)
          )}

          {this.renderSection(formatMessage(messages.notes), <ExperienceSectionList text={notes} />, !isNil(notes))}

          {this.renderSection(
            formatMessage(messages.pricingBreakdownTitle),
            this.renderPricingBreakdown(),
            this.canRenderPricingBreakdown
          )}

          {this.renderSection(
            formatMessage(messages.cancellationPolicy),
            <>
              <PolicyInfo>{formatMessage(messages.policyInfo)}</PolicyInfo>
              <PolicyLink onClick={this.handlePolicyClicked}>{formatMessage(messages.policyLink)}</PolicyLink>
            </>
          )}
        </DetailsWrapper>
      </Container>
    );
  }
}

export const LocationSection = compose(injectIntl)(LocationSectionComponent);
